import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { ToastService } from '../../../shared/toast/toast.service';
import { ErrorService } from '../../../shared/services/error-service';
import { WorkbookUploadService, UploadProgressEvent } from '../../../shared/services/workbook-upload.service';

import { AutoCompleteDropdownComponent } from '../../../shared/controls/auto-complete-dropdown/auto-complete-dropdown.component';

import { PerformanceService, PeriodService, InsurerService, ClientService, ClientAreaService, AllPeriodItem, AllInsurerItem } from '../../../../service/proxy';

@Component({
    selector: 'app-create-performance-batch',
    templateUrl: './create-performance-batch.component.html',
    styleUrls: ['./create-performance-batch.component.scss'],

    imports: [
        CommonModule,
        FormsModule,
        AutoCompleteDropdownComponent
    ],

    providers: [
        PerformanceService,
        InsurerService,
        PeriodService,
        ClientService,
        ClientAreaService
    ]
})
export class CreatePerformanceBatchComponent implements OnInit {
    public batchId: number = 0;
    public isSaving: boolean = false;
    public formSubmitted: boolean = false;
    public uploadProgress: number = 0;
    public uploadMessage: string = '';

    public selectedPeriodId: number | undefined = undefined;
    public periods: AllPeriodItem[] = [];

    public selectedInsurerId: number | undefined = undefined;
    public insurers: AllInsurerItem[] = [];

    public selectedFile: File | null = null;
    public selectedFileName: string = '';

    public showClientDropdown = false;
    public selectedClientId?: number;
    public clients: { id: number; name: string }[] = [];

    public selectedClientAreaId?: number;
    public clientAreas: { id: number; name: string }[] = [];

    constructor(
         private router: Router,
         private performanceService: PerformanceService,
         private periodService: PeriodService,
         private insurerService: InsurerService,
         private clientService: ClientService,
         private clientAreaService: ClientAreaService,
         private errorService: ErrorService,
         private toastService: ToastService,
         private cdr: ChangeDetectorRef,
         private workbookUploadService: WorkbookUploadService) {}

    ngOnInit(): void {
        this.getPeriods();
        this.getInsurers();
    }

    getPeriods(): void {
        this.periodService.getAllPeriods()
            .subscribe((data) => {
                this.periods = data.results || [];
                this.selectedPeriodId = this.periods[1]?.id ?? 0;
            }
        );
    }

    getInsurers(): void {
        this.insurerService.getAllInsurers()
            .subscribe((data) => {
                this.insurers = data.results || [];
            }
        );
    }

    onPeriodChange(selectedId: number | null): void {
        this.selectedPeriodId = selectedId ?? undefined;
    }

    onInsurerChange(selectedId: number | null): void {
        this.selectedInsurerId = selectedId ?? undefined;

        const selected = this.insurers.find(i => i.id === this.selectedInsurerId);
        this.showClientDropdown = !!selected?.importAreaMandatory;

        if (this.showClientDropdown) {
            this.clientService.getAllClients()
                .subscribe({
                    next: (data) => {
                        this.clients = data.results.map(c => ({ id: c.id, name: c.clientName }));
                    },
                    error: (err) => this.errorService.handleError(err)
                }
            );
        }
        else {
            this.selectedClientId = undefined;
            this.clients = [];
            this.clientAreas = [];
        }
    }

    onClientChange(selectedId: number | null): void {
        this.selectedClientId = selectedId ?? undefined;
        this.clientAreas = [];
        this.selectedFile = null;

        if (!this.selectedClientId || !this.selectedInsurerId) {
            return;
        }

        this.clientAreaService.getClientAreasByClientAndInsurer(this.selectedClientId, this.selectedInsurerId)
            .subscribe({
                next: (response) => {
                    this.clientAreas = response.results.map(a => ({ id: a.id, name: a.areaName })) || [];

                    if (this.clientAreas.length === 1) {
                        this.selectedClientAreaId = this.clientAreas[0].id;
                    }
                    else {
                        this.selectedClientAreaId = undefined;
                    }
                },
                error: (err) => this.errorService.handleError(err)
            }
        );
    }

    onClientAreaChange(selectedId: number | null): void {
        this.selectedClientAreaId = selectedId ?? undefined;
    }

    displayPeriod(option: any): string {
        const padded = ('0' + option?.month).slice(-2);
        return `${option?.year}-${padded}`;
    }

    displayInsurer(option: any): string {
        return option?.insurerName || '';
    }

    displayClient(option: any): string {
        return option?.name || '';
    }

    displayClientArea(option: any): string {
        return option?.name || '';
    }

    onFileSelected(event: any): void {
        const input = event.target as HTMLInputElement;

        if (input.files && input.files.length > 0) {
            this.selectedFile = input.files[0];
            this.selectedFileName = input.files[0].name;
        }
        else {
            this.selectedFile = null;
            this.selectedFileName = '';
        }
    }

    onSave(form: any): void {
        this.formSubmitted = true;

        if (form.invalid || !this.selectedFile) {
            return;
        }

        this.isSaving = true;
        this.uploadProgress = 0;
        this.uploadMessage = 'Preparing upload...';

        this.workbookUploadService.upload({
            importer: 'performance',
            file: this.selectedFile,
            fields: {
                periodId: this.selectedPeriodId,
                insurerId: this.selectedInsurerId,
                clientId: this.selectedClientId,
                clientAreaId: this.selectedClientAreaId,
            }
        }).subscribe({
            next: (event: UploadProgressEvent) => {
                this.uploadProgress = event.progress;
                this.uploadMessage = event.message;
                this.cdr.detectChanges();

                if (event.progress === 100) {
                    if (event.isExisting) {
                        this.toastService.show('Cannot upload — a pending import exists for this insurer, client, policy and period. Delete it first.', 'info');
                        this.isSaving = false;
                        return;
                    }

                    sessionStorage.setItem('isRedirected', true.toString());
                    this.router.navigate([`/performance-wizard/${event.batchId}`]);
                }
            },
            error: (error: Error) => {
                if (error.message?.includes('xlsx')) {
                    this.toastService.show('Invalid file extension. Only .xlsx files are allowed.', 'warning');
                }
                else {
                    this.toastService.show(error.message || 'Upload failed. Please try again.', 'error');
                }

                this.isSaving = false;
                this.uploadProgress = 0;
                this.uploadMessage = '';
            }
        });
    }

    onCancel(): void {
        this.router.navigate([`/performance`]);
    }
}
